import React, { useMemo } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

const AlertsFilterBar = ({ 
  meters, 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  sources,
  financialYearOptions,
  currentFinancialYear 
}) => {
  // Get sub-locations from selected sources
  const availableSubLocations = useMemo(() => {
    if (filters.sourceIds.length === 0) {
      // If no source selected, show all sub-locations
      const allSubLocs = sources.flatMap(s => s.subLocations || []);
      return Array.from(
        new Map(allSubLocs.map(sl => [sl.value, sl])).values()
      );
    }
    
    const selectedSources = sources.filter(s => filters.sourceIds.includes(s.value));
    const subLocs = selectedSources.flatMap(s => s.subLocations || []);
    
    const uniqueSubLocs = Array.from(
      new Map(subLocs.map(sl => [sl.value, sl])).values()
    );
    
    return uniqueSubLocs;
  }, [filters.sourceIds, sources]);

  // Get available modules based on filters
  const availableModules = useMemo(() => {
    let filteredMeters = meters;

    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    const modules = new Set();
    filteredMeters.forEach(meter => {
      const currentKpi = meter.financialYearKpis?.find(
        fykpi => fykpi.financialYear === filters.financialYear
      )?.kpi;
      if (currentKpi?.module) modules.add(currentKpi.module);
    });

    return Array.from(modules).map(m => ({ value: m, label: m }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.financialYear]);

  // Get available categories based on filters
  const availableCategories = useMemo(() => {
    let filteredMeters = meters;

    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    if (filters.modules.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.modules.includes(kpi?.module);
      });
    }

    const categories = new Set();
    filteredMeters.forEach(meter => {
      const currentKpi = meter.financialYearKpis?.find(
        fykpi => fykpi.financialYear === filters.financialYear
      )?.kpi;
      if (currentKpi?.category) categories.add(currentKpi.category);
    });

    return Array.from(categories).map(c => ({ value: c, label: c }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.financialYear]);

  // Get available sub-categories based on filters
  const availableSubCategories = useMemo(() => {
    let filteredMeters = meters;

    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    if (filters.modules.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.modules.includes(kpi?.module);
      });
    }

    if (filters.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.categories.includes(kpi?.category);
      });
    }

    const subCategories = new Set();
    filteredMeters.forEach(meter => {
      const currentKpi = meter.financialYearKpis?.find(
        fykpi => fykpi.financialYear === filters.financialYear
      )?.kpi;
      if (currentKpi?.subCategory) subCategories.add(currentKpi.subCategory);
    });

    return Array.from(subCategories).map(sc => ({ value: sc, label: sc }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.financialYear]);

  // Get available KPIs based on filters
  const availableKpis = useMemo(() => {
    let filteredMeters = meters;

    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    if (filters.modules.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.modules.includes(kpi?.module);
      });
    }

    if (filters.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.categories.includes(kpi?.category);
      });
    }

    if (filters.subCategories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.subCategories.includes(kpi?.subCategory);
      });
    }

    const kpis = new Set();
    filteredMeters.forEach(meter => {
      const currentKpi = meter.financialYearKpis?.find(
        fykpi => fykpi.financialYear === filters.financialYear
      )?.kpi;
      if (currentKpi?.kpi) kpis.add(currentKpi.kpi);
    });

    return Array.from(kpis).map(k => ({ value: k, label: k }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, filters.financialYear]);

  // Get available meter IDs based on filters
  const availableMeterIds = useMemo(() => {
    let filteredMeters = meters;

    if (filters.sourceIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => filters.sourceIds.includes(m.sourceId));
    }

    if (filters.subLocationIds.length > 0) {
      filteredMeters = filteredMeters.filter(m => 
        filters.subLocationIds.includes(m.subLocationId)
      );
    }

    if (filters.modules.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.modules.includes(kpi?.module);
      });
    }

    if (filters.categories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.categories.includes(kpi?.category);
      });
    }

    if (filters.subCategories.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.subCategories.includes(kpi?.subCategory);
      });
    }

    if (filters.kpis.length > 0) {
      filteredMeters = filteredMeters.filter(m => {
        const kpi = m.financialYearKpis?.find(
          fykpi => fykpi.financialYear === filters.financialYear
        )?.kpi;
        return filters.kpis.includes(kpi?.kpi);
      });
    }

    const meterMap = new Map();

    filteredMeters.forEach(meter => {
      if (!meter.meterId) return;

      // keep first occurrence (or overwrite if you prefer)
      if (!meterMap.has(meter.meterId)) {
        meterMap.set(meter.meterId, meter.meterName || meter.meterId);
      }
    });

    return Array.from(meterMap.entries()).map(([id, name]) => ({
      value: id,
      label: name,
    }));

  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, filters.kpis, filters.financialYear]);

  const handleFilterChange = (field, value) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const hasActiveFilters = 
    filters.sourceIds.length > 0 ||
    filters.subLocationIds.length > 0 ||
    filters.modules.length > 0 ||
    filters.categories.length > 0 ||
    filters.subCategories.length > 0 ||
    filters.kpis.length > 0 ||
    filters.meterIds.length > 0 ||
    filters.status !== null ||
    filters.financialYear !== currentFinancialYear;

  const statusOptions = [
    { value: 'OPEN', label: 'Open' },
    { value: 'RESOLVED', label: 'Resolved' },
    { value: 'ACK', label: 'Acknowledged' }
  ];

  return (
    <Card className="filter-card mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filters
          </h6>
          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onClearFilters}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Clear All
            </Button>
          )}
        </div>

        <Row>
          <Col md={3} className="mb-3">
            <Form.Label>Financial Year</Form.Label>
            <Select
              options={financialYearOptions}
              value={financialYearOptions.find(fy => fy.value === filters.financialYear)}
              onChange={(selected) => handleFilterChange('financialYear', selected?.value || currentFinancialYear)}
              placeholder="Select financial year..."
              isClearable={false}
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Status</Form.Label>
            <Select
              options={statusOptions}
              value={statusOptions.find(s => s.value === filters.status)}
              onChange={(selected) => handleFilterChange('status', selected?.value || null)}
              placeholder="All statuses..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Location</Form.Label>
            <Select
              isMulti
              options={sources}
              value={sources.filter(s => filters.sourceIds.includes(s.value))}
              onChange={(selected) => handleFilterChange('sourceIds', selected.map(s => s.value))}
              placeholder="All locations..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Sub Location</Form.Label>
            <Select
              isMulti
              options={availableSubLocations}
              value={availableSubLocations.filter(sl => filters.subLocationIds.includes(sl.value))}
              onChange={(selected) => handleFilterChange('subLocationIds', selected.map(sl => sl.value))}
              placeholder="All sub locations..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Module</Form.Label>
            <Select
              isMulti
              options={availableModules}
              value={availableModules.filter(m => filters.modules.includes(m.value))}
              onChange={(selected) => handleFilterChange('modules', selected.map(m => m.value))}
              placeholder="All modules..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Category</Form.Label>
            <Select
              isMulti
              options={availableCategories}
              value={availableCategories.filter(c => filters.categories.includes(c.value))}
              onChange={(selected) => handleFilterChange('categories', selected.map(c => c.value))}
              placeholder="All categories..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>Sub Category</Form.Label>
            <Select
              isMulti
              options={availableSubCategories}
              value={availableSubCategories.filter(sc => filters.subCategories.includes(sc.value))}
              onChange={(selected) => handleFilterChange('subCategories', selected.map(sc => sc.value))}
              placeholder="All sub categories..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"               
            />
          </Col>

          <Col md={3} className="mb-3">
            <Form.Label>KPI</Form.Label>
            <Select
              isMulti
              options={availableKpis}
              value={availableKpis.filter(k => filters.kpis.includes(k.value))}
              onChange={(selected) => handleFilterChange('kpis', selected.map(k => k.value))}
              placeholder="All KPIs..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>

          <Col md={12} className="mb-3">
            <Form.Label>Meter ID</Form.Label>
            <Select
              isMulti
              options={availableMeterIds}
              value={availableMeterIds.filter(m => filters.meterIds.includes(m.value))}
              onChange={(selected) => handleFilterChange('meterIds', selected.map(m => m.value))}
              placeholder="All meters..."
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
              closeMenuOnSelect={false}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default AlertsFilterBar;